import { loadSnapshot, createShapeId } from 'tldraw';

import {
  countEditorAnnotations,
  countSnapshotAnnotations,
  countSnapshotArrowBindings,
  ensureFramesFromBoardMeta,
  framesFromEditor,
  mergePlacementsFromShapes,
  mergePreferLocalPlacements,
  preferRicherAnnotationSnapshot,
  preferRicherPlacements,
  shouldPersistBoardSnapshot,
  shouldPersistPlacements,
} from './boardSnapshotGuards';
import { getActivityId, getPinCardSize, normalizePlacements } from './autoLayout';
import { normalizeLogicalFrameId } from './frameMembership';

export {
  countEditorAnnotations,
  countSnapshotAnnotations,
  countSnapshotArrowBindings,
  ensureFramesFromBoardMeta,
  framesFromEditor,
  mergePlacementsFromShapes,
  mergePreferLocalPlacements,
  preferRicherAnnotationSnapshot,
  preferRicherPlacements,
  shouldPersistBoardSnapshot,
  shouldPersistPlacements,
};

/** tldraw text shapes have auto height — `props.h` is invalid and crashes Store.put. */
function sanitizeStoreRecord(record) {
  if (record?.typeName !== 'shape' || record?.type !== 'text') return record;
  if (record.props == null || !Object.prototype.hasOwnProperty.call(record.props, 'h')) {
    return record;
  }
  const { h: _removed, ...props } = record.props;
  return { ...record, props };
}

function pageIdFromStore(store, editor) {
  for (const record of Object.values(store || {})) {
    if (record?.typeName === 'page' && record?.id) return record.id;
  }
  return editor?.getCurrentPageId?.() || null;
}

/**
 * Minimal pin-card records so arrow bindings to pins stay valid during
 * loadSnapshot (pins are otherwise stripped from the persisted snapshot and
 * recreated later via syncPinShapes).
 */
export function pinStubRecordsFromBoard(activities, board, pageId) {
  if (!pageId || !board?.placements?.length) return [];

  const placements = normalizePlacements(activities || [], board.placements);
  const stubs = [];
  placements.forEach((placement, index) => {
    const activityId = String(placement.activityId);
    const activity = (activities || []).find(a => getActivityId(a) === activityId);
    const { width, height } = getPinCardSize(activity, placement.width, {
      manualResize: Boolean(placement.manualSize),
    });
    const frameId =
      placement.frameId == null ? null : normalizeLogicalFrameId(placement.frameId);
    // Fractional indices a1, a2, … keep stubs ordered without colliding.
    const indexKey = `a${(index + 1).toString(36)}`;
    stubs.push({
      id: createShapeId(`pin:${activityId}`),
      typeName: 'shape',
      type: 'pin-card',
      x: Number(placement.x) || 0,
      y: Number(placement.y) || 0,
      rotation: 0,
      index: indexKey,
      parentId: pageId,
      isLocked: false,
      opacity: 1,
      meta: { activityId, frameId },
      props: {
        w: width > 0 ? width : 300,
        h: height > 0 ? height : 400,
      },
    });
  });
  return stubs;
}

/**
 * Document-only snapshot for board persistence.
 * Uses store.getStoreSnapshot() (not getSnapshot) so session readiness cannot block saves.
 * Pin-cards are omitted — placements remain the source of truth for pin layout.
 * Arrow bindings that reference pin ids are kept; hydrate re-seeds pin stubs first.
 */
export function extractBoardDocumentSnapshot(editor) {
  if (!editor?.store?.getStoreSnapshot) return null;
  const document = editor.store.getStoreSnapshot('document');
  if (!document?.store) return document;

  const store = {};
  for (const [id, record] of Object.entries(document.store)) {
    if (record?.typeName === 'shape' && record?.type === 'pin-card') continue;
    store[id] = sanitizeStoreRecord(record);
  }

  return { ...document, store };
}

export function hydrateBoardDocument(editor, documentSnapshot, { activities, board } = {}) {
  if (!editor?.store || !documentSnapshot?.store || !Object.keys(documentSnapshot.store).length) {
    return;
  }

  const store = {};
  for (const [id, record] of Object.entries(documentSnapshot.store)) {
    if (record?.typeName === 'shape' && record?.type === 'pin-card') continue;
    store[id] = sanitizeStoreRecord(record);
  }

  const pageId = pageIdFromStore(store, editor);
  for (const stub of pinStubRecordsFromBoard(activities, board, pageId)) {
    store[stub.id] = stub;
  }

  // Pass a TLStoreSnapshot so tldraw runs schema migration before put.
  loadSnapshot(editor.store, {
    store,
    schema: documentSnapshot.schema || editor.store.schema.serialize(),
  });
}
